import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useWindowSize } from 'hooks'
import { ClickAwayListener } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { STATE_KEYS } from 'state/requests'
import { selectRequestsAreLoading } from 'state'
import { selectEditingCampaign } from 'state/campaigns'
import { selectCampaignComments, ThreadCommentState } from 'state/comments'
import { scrollSelectedCommentIntoView } from 'components/utils/comments'
import { useIdentifiersMap, useToggleCampaignEditorDrawer, useSetIdentifierRoute } from 'hooks/comment'
import { default as OldIcon } from 'components/Icon/Icon'

import FieldCommentsCard from 'components/Shared/Comment/FieldCommentCards'
import qs from 'query-string'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import IconButton from 'particles/Button/IconButton'
import Dropdown from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { TFunction } from 'i18next'
import { selectTemplateData } from 'state/campaignTemplates'

interface CommentCenterProps {
    className?: string
}

type commentFilterType = 'open' | 'resolved' | 'all'
const commentFilterValues: commentFilterType[] = ['open', 'resolved', 'all']
const commentFilterKeys = (t: TFunction, filter: commentFilterType) => {
    const keys: Record<string, string> = {
        open: t('campaignEditor.comment.openComment'),
        resolved: t('campaignEditor.comment.resolvedComment'),
        all: t('campaignEditor.comment.allComments'),
    }
    return keys[filter]
}

const CommentCenter: React.FC<CommentCenterProps> = ({ className }) => {
    const [t] = useTranslation()
    const closeBtn = useRef<HTMLButtonElement | null>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const [currentIdentifier, setCurrentIdentifier] = useState<string | null>(null)
    const [isElementVisible, setIsElementVisible] = useState(false)
    const [filter, setFilter] = useState<commentFilterType>('open')
    const [hasThreads, setHasThreads] = useState(false)
    const templateData = useSelector(selectTemplateData)
    const windowSize = useWindowSize()
    const location = useLocation()
    const match = useRouteMatch()
    const params = qs.parse(location.search)
    const { drawer, selectedIdentifier: identifier, selectedThread, highlightSelected } = params
    const prefix = match && (templateData ? '/campaign/template' : match.path)
    const selectedIdentifier = identifier && typeof identifier === 'string' ? identifier : null
    const editingCampaign = useSelector(selectEditingCampaign)
    const campaignUuid = editingCampaign?.uuid
    const comments = useSelector(selectCampaignComments(campaignUuid))
    const currentSection = prefix && location.pathname.replace(prefix, '').replace('/', '')
    const isLoading = useSelector(
        selectRequestsAreLoading([
            STATE_KEYS.GetCampaignComments,
            STATE_KEYS.DeleteCampaignComment,
            STATE_KEYS.ResolveCampaignThread,
        ]),
    )
    const [updateRoute] = useSetIdentifierRoute()
    const updateToggleCampaignEditorDrawer = useToggleCampaignEditorDrawer()

    const {
        tasksVerifiedComments,
        publishVerifiedComments,
        registrationVerifiedComments,
        campaignTemplateVerifiedComments,
    } = useIdentifiersMap()

    const sectionsComments: Record<
        string,
        { identifier: string; isFieldExist: boolean; labelPrefix?: string; threads: ThreadCommentState[] }[]
    > | null = useMemo(() => {
        if (comments) {
            const returnComments = {
                registration: registrationVerifiedComments,
                tasks: tasksVerifiedComments,
                publish: publishVerifiedComments,
                templateSelector: campaignTemplateVerifiedComments.templateSelector,
                templateDetail: campaignTemplateVerifiedComments.templateDetail,
                templateSummary: campaignTemplateVerifiedComments.templateSummary,
            }
            return returnComments
        } else {
            return null
        }
    }, [
        comments,
        tasksVerifiedComments,
        publishVerifiedComments,
        registrationVerifiedComments,
        campaignTemplateVerifiedComments,
    ])

    useEffect(() => {
        setCurrentIdentifier(selectedIdentifier)
        if (selectedIdentifier && sectionsComments) {
            const commentCenterElement =
                document.querySelector(`[data-id="${selectedIdentifier}-${selectedThread}"]`) ||
                document.querySelector(`[data-id^="${selectedIdentifier}"]`)

            if (!isElementVisible && commentCenterElement && !isLoading) {
                scrollSelectedCommentIntoView(commentCenterElement, 'center')
            }
        }
    }, [
        selectedIdentifier,
        sectionsComments,
        isLoading,
        isElementVisible,
        registrationVerifiedComments,
        campaignTemplateVerifiedComments,
        selectedThread,
    ])

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsElementVisible(entry.isIntersecting)
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 1,
            },
        )

        if (sectionsComments && selectedIdentifier) {
            const commentCenterElement = document.querySelector(`[data-id="${selectedIdentifier}-${selectedThread}"]`)

            if (commentCenterElement) {
                observer.observe(commentCenterElement)
            }
        }

        return () => {
            if (sectionsComments && selectedIdentifier) {
                const commentCenterElement = document.querySelector(
                    `[data-id="${selectedIdentifier}-${selectedThread}"]`,
                )
                if (commentCenterElement) {
                    observer.unobserve(commentCenterElement)
                }
            }
        }
    }, [sectionsComments, selectedIdentifier, selectedThread])

    const hasComments = currentSection && sectionsComments && sectionsComments[currentSection]?.length > 0

    useEffect(() => {
        setHasThreads(listRef.current !== null && listRef.current?.childNodes?.length > 0)
    }, [filter, sectionsComments])

    return (
        <div
            className={`${className} ${drawer === 'comments' ? 'show' : ''}`}
            onClick={(e: any) => {
                if (e.target?.id === 'comment-center-outer-container' && windowSize && windowSize < 768) {
                    updateToggleCampaignEditorDrawer('comments')
                }
            }}
            id="comment-center-outer-container"
        >
            <div className="comment-center-container">
                <div className="comment-center-title-block">
                    <Text scale={windowSize && windowSize < 768 ? 'H3' : 'H4'}>
                        {t('campaignEditor.comment.commentCenterTitle')}
                    </Text>
                    <Dropdown
                        width={140}
                        size="xs"
                        defaultValue={'open'}
                        value={filter}
                        onChange={value => setFilter(value as commentFilterType)}
                    >
                        {commentFilterValues.map((type, index) => (
                            <MenuItem.Text
                                key={`commentCenter-dropdown-${type}`}
                                text={commentFilterKeys(t, type)}
                                value={type}
                                tabIndex={index}
                            />
                        ))}
                    </Dropdown>
                </div>

                <ClickAwayListener
                    onClickAway={e => {
                        const target = e.target as HTMLElement
                        if (
                            target.id !== 'show-comment-center-button' &&
                            !target.closest('#show-comment-center-button')
                        ) {
                            setCurrentIdentifier(null)
                            updateRoute(null)
                        }
                    }}
                >
                    <div className="comments-container">
                        {windowSize && windowSize < 768 && (
                            <IconButton
                                form="icon"
                                size="s"
                                color="neutral"
                                icon="x"
                                iconForm="regular"
                                className="close-icon"
                                type="button"
                                onClick={e => {
                                    e.stopPropagation()
                                    updateToggleCampaignEditorDrawer('comments')
                                }}
                                ref={closeBtn}
                            />
                        )}
                        <div className="comments-block">
                            {!hasThreads && (
                                <div className="no-comments">
                                    <OldIcon.NotFoundIcon />
                                    <Text scale="H4" className="comment-title" style={{ whiteSpace: 'nowrap' }}>
                                        {t('emptyState.noCommentsTitle')}
                                    </Text>
                                </div>
                            )}
                            {hasComments && (
                                <div ref={listRef}>
                                    {sectionsComments[currentSection].map(
                                        (fieldComments: {
                                            identifier: string
                                            isFieldExist: boolean
                                            labelPrefix?: string
                                            threads: ThreadCommentState[]
                                        }) => {
                                            return fieldComments.threads.map(thread => {
                                                const show =
                                                    filter === 'all'
                                                        ? true
                                                        : filter === 'resolved'
                                                          ? thread.resolved_at !== null
                                                          : thread.resolved_at === null

                                                if (show) {
                                                    return (
                                                        <div
                                                            key={thread.uuid}
                                                            className="card"
                                                            data-id={`${fieldComments.identifier}-${thread.uuid}`}
                                                            onClick={() => {
                                                                const fieldElement = document.getElementById(
                                                                    fieldComments.identifier,
                                                                )
                                                                setCurrentIdentifier(fieldComments.identifier)
                                                                if (fieldElement) {
                                                                    scrollSelectedCommentIntoView(
                                                                        fieldElement,
                                                                        'center',
                                                                    )
                                                                }
                                                                updateRoute({
                                                                    identifier: fieldComments.identifier,
                                                                    threadId: thread.uuid,
                                                                    highlightSelected: true,
                                                                    taskUuid: null,
                                                                    stepUuid: null,
                                                                    section: null,
                                                                })
                                                                if (
                                                                    closeBtn.current &&
                                                                    windowSize &&
                                                                    windowSize < 768
                                                                ) {
                                                                    //delay closing center to let tasks section has time to switch tab and scroll
                                                                    setTimeout(() => {
                                                                        closeBtn.current?.click()
                                                                    }, 500)
                                                                }
                                                            }}
                                                        >
                                                            <FieldCommentsCard
                                                                fieldComments={thread.comments}
                                                                identifier={fieldComments.identifier}
                                                                cardClassName={`comment-card ${
                                                                    (highlightSelected &&
                                                                        thread.uuid === selectedThread) ||
                                                                    (!highlightSelected &&
                                                                        currentIdentifier === fieldComments.identifier)
                                                                        ? 'card-activated'
                                                                        : ''
                                                                }`}
                                                                isFieldDeleted={!fieldComments.isFieldExist}
                                                                cardWidth={windowSize <= 768 ? '100%' : 376}
                                                                onDeleteCommentThread={() => updateRoute(null)}
                                                                isResolved={Boolean(thread.resolved_at)}
                                                                threadId={thread.uuid}
                                                                labelPrefix={fieldComments.labelPrefix}
                                                            />
                                                        </div>
                                                    )
                                                } else {
                                                    return null
                                                }
                                            })
                                        },
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </ClickAwayListener>
            </div>
        </div>
    )
}

export default styled(CommentCenter)`
    width: 0px;
    transition: width 0.3s ease-in-out;
    height: 100%;
    overflow-y: hidden;
    background-color: ${({ theme }) => theme.colors.surface.primary};
    width: 0px;
    transition: width 0.3s ease-in-out;
    .comment-center-container {
        height: 100%;
        box-sizing: border-box;
        display: flex;
        overflow-y: hidden;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        border-radius: 4px;
        background-color: ${({ theme }) => theme.colors.surface.primary};
    }
    .comment-center-title-block {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 12px 0px;
    }
    .comments-container {
        height: 100%;
        width: 100%;
        overflow-y: hidden;
    }
    &.show {
        width: 400px;
        margin-right: 24px;
    }
    .comments-block {
        box-sizing: border-box;
        height: fit-content;
        max-height: 100%;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 0px 12px 12px;
        ::-webkit-scrollbar {
            -webkit-appearance: none;
            width: 7px;
        }

        ::-webkit-scrollbar-thumb {
            border-radius: 4px;
            background-color: rgba(0, 0, 0, 0.5);
            box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
        }
    }
    .comment-card {
        cursor: pointer;
        /* max-height: fit-content; */
    }
    .comment-card:hover {
        .card-item {
            background-color: transparent;
        }
        .textarea-input-container {
            background-color: ${({ theme }) => theme.colors.surface.primary};
        }
        background-color: ${({ theme }) => theme.colors.surface.neutralActionSubPastelHover};
        border-color: ${({ theme }) => theme.colors.border.primary};
    }
    .card:not(:last-child) {
        margin-bottom: 10px;
    }
    .card-activated {
        .card-item {
            background-color: transparent;
        }
        border-color: ${({ theme }) => theme.colors.border.warning};
        background-color: ${({ theme }) => theme.colors.surface.warningPastel};
        .textarea-input-container {
            background-color: ${({ theme }) => theme.colors.surface.primary};
        }
        &:hover {
            border-color: ${({ theme }) => theme.colors.border.warning};
            background-color: ${({ theme }) => theme.colors.surface.warningPastel};
        }
    }
    .no-comments {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding-top: ${({ theme }) => theme.spacing[800]};
    }
    @media screen and (max-width: 768px) {
        position: fixed;
        left: 0;
        z-index: 999;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        width: 100%;
        display: flex;
        visibility: collapse;
        flex-direction: column;
        justify-content: flex-end;
        &.show {
            width: 100%;
            visibility: visible;
            margin-right: 0;
        }
        .comment-center-container {
            position: relative;
            height: 90%;
            max-height: 90%;
            gap: 24px;
            padding-top: 40px;
            border-radius: 0px;
        }
        .close-icon {
            position: absolute;
            right: 12px;
            top: 16px;
        }
    }
`
